import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Send, Image as ImageIcon, AlertCircle, Wrench, Zap, Paintbrush, Hammer } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import api from "../lib/api";
import { ImagesField } from './ui/images-field';
import { WhatsAppButton } from './ui/whatsapp-button';
import { WHATSAPP_TEMPLATES } from '../lib/whatsapp';
import { businessInfo } from '../mock';


const categoryIcons = {
  Plumber: Wrench,
  Electrician: Zap,
  Painter: Paintbrush,
  Carpenter: Hammer
};

const ServiceRequest = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);

  useEffect(() => {
    document.title = 'Home Services | Plumber, Electrician, Painter - CHIV Properties Greater Noida';
  }, []);

  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    category: 'Plumber',
    problemTitle: '',
    problemDescription: '',
    location: '',
    images: [],
    urgency: 'normal'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.customerPhone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const requestData = {
        ...formData,
        images: formData.images,
      };
      
      const response = await api.post(`/service-requests`, requestData);
      
      toast.success('Service request submitted successfully! Our service providers will contact you soon.');
      setRequestId(response.data.id);
      setSubmitted(true);
      
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceCategories = [
    { value: 'Plumber', label: 'Plumber', desc: 'Pipeline, bathroom, kitchen issues' },
    { value: 'Electrician', label: 'Electrician', desc: 'Wiring, switches, MCB issues' },
    { value: 'Painter', label: 'Painter', desc: 'Wall painting, whitewash' },
    { value: 'Carpenter', label: 'Carpenter', desc: 'Wood work, furniture repair' }
  ];

  if (submitted && requestId) {
    const trackUrl = `${window.location.origin}/service-request/${requestId}`;
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center" data-testid="service-request-success">
        <Card className="max-w-lg mx-4">
          <CardContent className="p-8 text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Request Submitted!</h2>
            <p className="text-gray-600 mb-2">Your service request has been sent to our {formData.category.toLowerCase()} providers. You'll receive quotes soon at <span className="font-semibold">{formData.customerPhone}</span>.</p>
            <p className="text-sm text-gray-500 mb-6">Save the tracking link below — you can come back any time to review quotes.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={() => navigate(`/service-request/${requestId}`)} className="bg-blue-600 hover:bg-blue-700" data-testid="track-request-btn">
                <AlertCircle className="h-4 w-4 mr-2" /> Track Request
              </Button>
              <WhatsAppButton
                phone={formData.customerPhone}
                message={WHATSAPP_TEMPLATES.customerTrackRequest({
                  customerName: formData.customerName,
                  problemTitle: formData.problemTitle,
                  url: trackUrl,
                })}
                label="Send to my WhatsApp"
                size="default"
                data-testid="whatsapp-share-btn"
              />
              <Button onClick={() => navigate('/')} variant="outline" data-testid="go-home-btn">
                <Home className="h-4 w-4 mr-2" /> Go Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Home className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{businessInfo.name}</h1>
                <p className="text-xs text-gray-600 italic">{businessInfo.tagline}</p>
              </div>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Request Service & Get <span className="text-blue-600">Multiple Quotes</span>
            </h1>
            <p className="text-lg text-gray-600">
              Submit your problem with photos. Multiple service providers will send you quotes. Choose the best!
            </p>
          </div>

          {/* Service Categories */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            {serviceCategories.map((cat) => {
              const Icon = categoryIcons[cat.value];
              return (
                <Card 
                  key={cat.value} 
                  className={`cursor-pointer transition-all ${formData.category === cat.value ? 'border-2 border-blue-600 bg-blue-50' : 'hover:shadow-md'}`}
                  onClick={() => setFormData({...formData, category: cat.value})}
                  data-testid={`category-${cat.value.toLowerCase()}`}
                >
                  <CardContent className="p-4 text-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${formData.category === cat.value ? 'bg-blue-200' : 'bg-gray-100'}`}>
                      <Icon className={`h-6 w-6 ${formData.category === cat.value ? 'text-blue-700' : 'text-gray-600'}`} />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{cat.label}</h3>
                    <p className="text-xs text-gray-600">{cat.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Request Form */}
          <Card>
            <CardHeader>
              <CardTitle>Submit Your Service Request</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="service-request-form">
                {/* Customer Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Your Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Your Name *</label>
                      <Input name="customerName" value={formData.customerName} onChange={handleInputChange} placeholder="Enter your name" required data-testid="customer-name-input" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Phone Number *</label>
                      <Input name="customerPhone" value={formData.customerPhone} onChange={handleInputChange} placeholder="9876543210" required data-testid="customer-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Email (Optional)</label>
                      <Input name="customerEmail" type="email" value={formData.customerEmail} onChange={handleInputChange} placeholder="your@email.com" data-testid="customer-email-input" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Location *</label>
                      <Input name="location" value={formData.location} onChange={handleInputChange} placeholder="Your address/area" required data-testid="location-input" />
                    </div>
                  </div>
                </div>

                {/* Problem Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Problem Details</h3>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Problem Title *</label>
                    <Input name="problemTitle" value={formData.problemTitle} onChange={handleInputChange} placeholder="e.g., Bathroom tap leaking" required data-testid="problem-title-input" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Detailed Description *</label>
                    <Textarea name="problemDescription" value={formData.problemDescription} onChange={handleInputChange} placeholder="Describe the problem in detail..." rows={4} required data-testid="problem-description-input" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Upload Problem Images</label>
                    <ImagesField
                      value={formData.images}
                      onChange={(urls) => setFormData((f) => ({ ...f, images: urls }))}
                      kind="service-request"
                      max={10}
                    />
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" />
                      Adding images helps providers give accurate quotes
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Urgency Level</label>
                    <Select name="urgency" value={formData.urgency} onValueChange={(value) => setFormData({...formData, urgency: value})}>
                      <SelectTrigger data-testid="urgency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">Urgent - Need immediate help</SelectItem>
                        <SelectItem value="normal">Normal - Within 2-3 days</SelectItem>
                        <SelectItem value="can_wait">Can Wait - Flexible timing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">What happens next?</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Multiple {formData.category.toLowerCase()}s will receive your request</li>
                      <li>They will review your problem and images</li>
                      <li>You'll receive quotes with estimated cost & time</li>
                      <li>Compare quotes and choose the best option</li>
                    </ul>
                  </div>
                </div>

                {/* Submit Button */}
                <Button type="submit" size="lg" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                  <Send className="h-5 w-5 mr-2" />
                  {isSubmitting ? 'Submitting...' : 'Submit Request & Get Quotes'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequest;
